import type { IPaymentV2Repository, PaginatedResponse } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
    Payment,
    PaymentAllocation,
    PaymentBankSnapshot,
    PaymentMethod,
    PaymentQueryParams,
    PaymentStatus,
    PaymentSummary,
    UploadPaymentProofDTO,
    VerifyPaymentDTO,
    CreateManualPaymentDTO,
    UpdateManualPaymentDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// API Response Types
interface ApiPaymentAllocationResponse {
  id: string;
  chargeId: string;
  amount: number;
  chargeConcept?: string;
  chargePeriod?: string | null;
  createdAt: string;
}

interface ApiPaymentResponse {
  id: string;
  memberId: string;
  unitId?: string | null;
  bankSnapshot?: PaymentBankSnapshot | null;
  amount: number;
  method: PaymentMethod | null;
  status: PaymentStatus;
  proofUrl: string | null;
  transactionReference: string | null;
  notes: string | null;
  paidAt: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  rejectionReason: string | null;
  invoiceUuid: string | null;
  xmlUrl: string | null;
  pdfUrl: string | null;
  invoicedAt: string | null;
  allocations: ApiPaymentAllocationResponse[];
  createdAt: string;
  updatedAt: string;
  memberName?: string;
  unitNumber?: string;
  allocatedAmount: number;
  unallocatedAmount: number;
}

interface ApiPaymentSummaryResponse {
  totalVerifying: number;
  totalVerified: number;
  totalRejected: number;
  amountVerified: number;
  amountPending: number;
}

// Mappers
const mapApiAllocationToAllocation = (apiAllocation: ApiPaymentAllocationResponse): PaymentAllocation => ({
  id: apiAllocation.id,
  chargeId: apiAllocation.chargeId,
  amount: apiAllocation.amount,
  chargeConcept: apiAllocation.chargeConcept,
  chargePeriod: apiAllocation.chargePeriod,
  createdAt: apiAllocation.createdAt,
});

const mapApiPaymentToPayment = (apiPayment: ApiPaymentResponse): Payment => ({
  id: apiPayment.id,
  memberId: apiPayment.memberId,
  unitId: apiPayment.unitId ?? null,
  bankSnapshot: apiPayment.bankSnapshot ?? null,
  amount: apiPayment.amount,
  method: apiPayment.method,
  status: apiPayment.status,
  proofUrl: apiPayment.proofUrl,
  transactionReference: apiPayment.transactionReference,
  notes: apiPayment.notes,
  paidAt: apiPayment.paidAt,
  verifiedAt: apiPayment.verifiedAt,
  verifiedBy: apiPayment.verifiedBy,
  rejectionReason: apiPayment.rejectionReason,
  invoiceUuid: apiPayment.invoiceUuid,
  xmlUrl: apiPayment.xmlUrl,
  pdfUrl: apiPayment.pdfUrl,
  invoicedAt: apiPayment.invoicedAt,
  allocations: apiPayment.allocations.map(mapApiAllocationToAllocation),
  createdAt: apiPayment.createdAt,
  updatedAt: apiPayment.updatedAt,
  memberName: apiPayment.memberName,
  unitNumber: apiPayment.unitNumber,
  allocatedAmount: apiPayment.allocatedAmount,
  unallocatedAmount: apiPayment.unallocatedAmount,
});

export class ApiPaymentV2Repository implements IPaymentV2Repository {
  // === Admin Queries ===

  async getAllPayments(params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      status: params?.status,
      method: params?.method,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder?.toLowerCase() as 'asc' | 'desc' | undefined,
    });
    const response = await client.get<PaginatedResponse<ApiPaymentResponse>>(
      `v1/payments-v2?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiPaymentToPayment),
    };
  }

  async getPendingVerification(): Promise<Payment[]> {
    const client = getApiClient();
    const response = await client.get<ApiPaymentResponse[]>('v1/payments-v2/pending-verification');
    return response.map(mapApiPaymentToPayment);
  }

  async getSummary(): Promise<PaymentSummary> {
    const client = getApiClient();
    return client.get<ApiPaymentSummaryResponse>('v1/payments-v2/summary');
  }

  async getPaymentById(id: string): Promise<Payment | null> {
    const client = getApiClient();
    try {
      const apiPayment = await client.get<ApiPaymentResponse>(`v1/payments-v2/${id}`);
      return mapApiPaymentToPayment(apiPayment);
    } catch {
      return null;
    }
  }

  async getMemberPayments(memberId: string, params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      status: params?.status,
    });
    const response = await client.get<PaginatedResponse<ApiPaymentResponse>>(
      `v1/payments-v2/member/${memberId}?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiPaymentToPayment),
    };
  }

  // === Resident Queries ===

  async getMyPayments(params?: PaymentQueryParams): Promise<PaginatedResponse<Payment>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      status: params?.status,
      unitId: params?.unitId,
    });
    const response = await client.get<PaginatedResponse<ApiPaymentResponse>>(
      `v1/payments-v2/my?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiPaymentToPayment),
    };
  }

  // === Commands ===

  async uploadProof(data: UploadPaymentProofDTO): Promise<Payment> {
    const client = getApiClient();
    const apiPayment = await client.post<ApiPaymentResponse>('v1/payments-v2/upload-proof', data);
    return mapApiPaymentToPayment(apiPayment);
  }

  async createManualPayment(data: CreateManualPaymentDTO): Promise<Payment> {
    const client = getApiClient();
    const apiPayment = await client.post<ApiPaymentResponse>('v1/payments-v2/manual', data);
    return mapApiPaymentToPayment(apiPayment);
  }

  async updateManualPayment(id: string, data: UpdateManualPaymentDTO): Promise<Payment> {
    const client = getApiClient();
    const apiPayment = await client.put<ApiPaymentResponse>(`v1/payments-v2/${id}/manual`, data);
    return mapApiPaymentToPayment(apiPayment);
  }

  async deleteManualPayment(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/payments-v2/${id}/manual`);
  }

  async verifyPayment(id: string, data: VerifyPaymentDTO): Promise<Payment> {
    const client = getApiClient();
    const apiPayment = await client.post<ApiPaymentResponse>(`v1/payments-v2/${id}/verify`, data);
    return mapApiPaymentToPayment(apiPayment);
  }

  async deletePayment(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/payments-v2/${id}`);
  }
}
